import { useEffect, useRef, useState } from 'react';
import logoIcon from '../assets/icons/MeinGruenraum_Logo.png';
import useAuth from '../hooks/useAuth';
import {
  getPathname,
  getSearch,
  replace as navReplace,
  replaceUrl,
} from '../utils/browserNav';

/**
 * VerifyPage — handles magic link token verification.
 *
 * Shown when the URL contains ?token=... (i.e. the user clicked the magic link).
 * On success, redirects to the main app by removing the token from the URL.
 *
 * The hasRun ref prevents double-invocation in React StrictMode (dev only),
 * where useEffect fires twice and the second call would see the token as
 * already consumed.
 */
const VerifyPage = () => {
  // 'verifying' | 'success' | 'error' — a missing token is known at mount,
  // so derive the initial status instead of setting it synchronously in the effect.
  const [status, setStatus] = useState(() => {
    const token = new URLSearchParams(getSearch()).get('token');
    return token ? 'verifying' : 'error';
  });
  const { verifyToken } = useAuth();
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const token = new URLSearchParams(getSearch()).get('token');
    if (!token) return;

    // SEC-08: strip the token from the URL right away — before the network
    // round-trip — so it never lingers in browser history, in access logs on a
    // reload, or in the Referer of any subresource. We stay on the verify page;
    // the success path below still navigates to '/'.
    replaceUrl(getPathname());

    verifyToken(token)
      .then((result) => {
        if (result.success) {
          setStatus('success');
          // Redirect to app root after short delay, clearing the token from URL
          setTimeout(() => {
            navReplace('/');
          }, 1500);
        } else {
          setStatus('error');
        }
      })
      .catch(() => setStatus('error'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-center gap-2 mb-8">
          <img src={logoIcon} alt="Mein Grünraum Logo" className="h-8 w-8 object-contain" />
          <span className="text-lg font-semibold text-gray-900">Mein Grünraum</span>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-6 text-center">
          {status === 'verifying' && (
            <p className="text-sm text-gray-600">Link wird überprüft…</p>
          )}

          {status === 'success' && (
            <>
              <h1 className="text-base font-medium text-emerald-600 mb-2">
                Erfolgreich angemeldet
              </h1>
              <p className="text-sm text-gray-600">Du wirst weitergeleitet…</p>
            </>
          )}

          {status === 'error' && (
            <>
              <h1 className="text-base font-medium text-red-600 mb-2">Link ungültig</h1>
              <p className="text-sm text-gray-600 mb-4">
                Dieser Link ist abgelaufen oder wurde bereits verwendet.
              </p>
              <a href="/login" className="text-sm text-emerald-600 hover:underline">
                Neuen Link anfordern
              </a>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyPage;
